import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/solid';
import handCarryService from './handCarryService';
import AirItemTypeService from '../setting/airItemType/AirItemTypeService';
import AirItemTypeTripleDropdown from '../../sharedComponents/AirItemTypeTripleDropdown';
import { formatAmountRound } from '../../utils/formatAmount';

const BRAND_TIER_LABEL = { normal: 'Normal', street_brand: 'Street', brand_name: 'Brand Name' };

const formatOrderDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '-';
  }
};

const HandCarryTypeAssignment = () => {
  const [handCarryItems, setHandCarryItems] = useState([]);
  const [airItemTypes, setAirItemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigningItems, setAssigningItems] = useState({}); // { handCarryId: [{ airItemTypeId, qty }] }
  const [savingId, setSavingId] = useState(null);
  const [hideSendOnly, setHideSendOnly] = useState(false);
  const [showAll, setShowAll] = useState(false); // Toggle to show items with assigned types for editing

  // Load data on mount and when showAll changes
  useEffect(() => {
    loadData(showAll);
  }, [showAll]);

  // Init form rows from handCarryItems (pre-populate with existing assignedTypes if editing)
  useEffect(() => {
    if (handCarryItems.length === 0) return;
    setAssigningItems(prev => {
      const next = { ...prev };
      handCarryItems.forEach(item => {
        // If item has existing assigned types, pre-populate; else default empty row
        if (item.assignedTypes?.length > 0 && !next[item.id]) {
          next[item.id] = item.assignedTypes.map(at => ({
            airItemTypeId: at.airItemTypeId,
            qty: at.qty || 1
          }));
        } else if (!next[item.id]) {
          next[item.id] = [{ airItemTypeId: '', qty: 1 }];
        }
      });
      return next;
    });
  }, [handCarryItems]);

  const loadData = async (includeAll = false) => {
    setLoading(true);
    setAssigningItems({}); // Clear form state when reloading
    try {
      const [handCarryRes, airItemRes] = await Promise.all([
        handCarryService.getHandCarryItemsWithoutTypes(includeAll),
        AirItemTypeService.getAirItemTypes({ isActive: true })
      ]);
      setHandCarryItems(handCarryRes.data?.data || []);
      setAirItemTypes(airItemRes.data?.data || []);
    } catch (err) {
      toast.error('ไม่สามารถโหลดข้อมูลได้');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Add item row
  const addItemRow = (handCarryId) => {
    setAssigningItems(prev => ({
      ...prev,
      [handCarryId]: [...(prev[handCarryId] || []), { airItemTypeId: '', qty: 1 }]
    }));
  };

  // Remove item row
  const removeItemRow = (handCarryId, index) => {
    setAssigningItems(prev => ({
      ...prev,
      [handCarryId]: prev[handCarryId].filter((_, i) => i !== index)
    }));
  };

  // Update item
  const updateItem = (handCarryId, index, field, value) => {
    setAssigningItems(prev => ({
      ...prev,
      [handCarryId]: prev[handCarryId].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Save assignment (per_kg: send qty 1; per_item: send actual qty)
  const handleSave = async (handCarryId) => {
    const items = assigningItems[handCarryId] || [];
    const validItems = items
      .filter(row => row.airItemTypeId)
      .map(row => {
        const type = airItemTypes.find(t => t.id === row.airItemTypeId);
        const qty = type?.calculationMethod === 'per_item' ? (row.qty || 1) : 1;
        return { airItemTypeId: row.airItemTypeId, qty };
      });

    if (validItems.length === 0) {
      toast.warning('กรุณาเลือกอย่างน้อย 1 ประเภท');
      return;
    }

    setSavingId(handCarryId);
    try {
      await handCarryService.assignTypesToHandCarry(handCarryId, validItems);
      toast.success('บันทึกสำเร็จ');
      if (showAll) {
        // When editing all items, update the item's assignedTypes in place
        setHandCarryItems(prev => prev.map(item => 
          item.id === handCarryId 
            ? { ...item, assignedTypes: validItems.map((vi, idx) => ({ ...vi, id: idx })) }
            : item
        ));
      } else {
        // When showing only unassigned, remove from list after save
        setHandCarryItems(prev => prev.filter(item => item.id !== handCarryId));
        setAssigningItems(prev => {
          const next = { ...prev };
          delete next[handCarryId];
          return next;
        });
      }
    } catch (err) {
      toast.error('ไม่สามารถบันทึกได้');
      console.error(err);
    } finally {
      setSavingId(null);
    }
  };

  // Get type display name
  const getTypeDisplay = (typeId) => {
    const type = airItemTypes.find(t => t.id === typeId);
    if (!type) return '';
    return `${type.category || ''} > ${type.type || ''} > ${type.typeName}`;
  };

  // Get type price
  const getTypePrice = (typeId) => {
    const type = airItemTypes.find(t => t.id === typeId);
    if (!type) return null;
    return {
      normal: parseFloat(type.amountPerUnit),
      sendOnly: parseFloat(type.sendOnlyAmountPerUnit) || parseFloat(type.amountPerUnit),
      unit: type.calculationMethod === 'per_kg' ? 'kg' : 'pcs',
      calculationMethod: type.calculationMethod
    };
  };

  // Filter: default show all; when hideSendOnly checked, hide sendOnly items
  const displayedItems = hideSendOnly
    ? handCarryItems.filter((item) => !item.order?.isSendOnly)
    : handCarryItems;

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">กำหนดประเภทสินค้าหิ้ว</h1>
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-bold">กำหนดประเภทสินค้าหิ้ว</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            แสดงรายการที่กำหนดแล้ว (แก้ไข)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={hideSendOnly}
              onChange={(e) => setHideSendOnly(e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            ซ่อน ฝากหิ้ว
          </label>
          <button
            onClick={() => loadData(showAll)}
            className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded border"
          >
            รีเฟรช
          </button>
        </div>
      </div>

      {displayedItems.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          {showAll ? 'ไม่มีรายการหิ้ว' : 'ไม่มีรายการที่รอกำหนดประเภท'}
        </div>
      ) : (
        <div className="space-y-3">
          {displayedItems.map((item) => {
            const isSendOnly = !!item.order?.isSendOnly;
            const hasExistingTypes = item.assignedTypes?.length > 0;
            return (
            <div key={item.id} className={`bg-white rounded-lg border shadow-sm overflow-visible ${hasExistingTypes ? 'ring-2 ring-green-200' : ''}`}>
              {/* Compact header: #id, order date, customer, URL */}
              <div className="px-4 py-2 bg-gray-50 border-b flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium text-gray-700 shrink-0">#{item.id}</span>
                <span className="text-sm text-gray-500 shrink-0">
                  {formatOrderDate(item.order?.orderDate)}
                </span>
                {hasExistingTypes && (
                  <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-800 rounded shrink-0">กำหนดแล้ว</span>
                )}
                {isSendOnly && (
                  <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded shrink-0">ฝากหิ้ว</span>
                )}
                <span className="text-sm text-gray-600 shrink-0">
                  {item.customer?.contact || item.customer?.smileName || '-'}
                </span>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-indigo-600 hover:underline truncate min-w-0"
                >
                  {item.url}
                </a>
              </div>

              {/* Always-expanded content */}
              <div className="p-4">
                {/* Previous Quotes (guideline) */}
                {item.priceQuotes?.length > 0 && (
                  <div className="mb-3 py-2 px-3 bg-amber-50 border border-amber-200 rounded text-sm">
                    <span className="font-medium text-amber-800 mr-2">ราคาที่เคยแจ้งลูกค้า:</span>
                    {item.priceQuotes.map((q) => {
                        const qNormal = parseFloat(q.airItemType?.amountPerUnit);
                        const qSendOnly = parseFloat(q.airItemType?.sendOnlyAmountPerUnit) || qNormal;
                        const qAmount = isSendOnly ? qSendOnly : qNormal;
                        const qUnit = q.airItemType?.calculationMethod === 'per_kg' ? 'kg' : 'pcs';
                        return (
                      <button
                        key={q.id}
                        type="button"
                        onClick={() => setAssigningItems(prev => ({
                          ...prev,
                          [item.id]: [{ airItemTypeId: q.airItemType?.id, qty: 1 }]
                        }))}
                        className="mr-3 text-amber-800 hover:underline text-left"
                      >
                        {q.airItemType?.typeName} — ฿{formatAmountRound(qAmount)}/{qUnit}
                        {(q.brand || q.brandTier || q.itemPriceJpy != null) && (
                          <span className="text-amber-700 font-normal">
                            {' '}({q.brand && `${q.brand.name} — ${BRAND_TIER_LABEL[q.brand.brandType] || q.brand.brandType}`}
                            {q.brand && q.itemPriceJpy != null && ', '}
                            {!q.brand && q.brandTier && (BRAND_TIER_LABEL[q.brandTier] || q.brandTier)}
                            {!q.brand && q.brandTier && q.itemPriceJpy != null && ', '}
                            {q.itemPriceJpy != null && `${Number(q.itemPriceJpy).toLocaleString()} JPY`})
                          </span>
                        )}
                      </button>
                    );})}
                  </div>
                )}

                {/* Rows: type + qty only when per_item */}
                <div className="space-y-2">
                  {(assigningItems[item.id] || []).map((row, idx) => {
                    const type = airItemTypes.find(t => t.id === row.airItemTypeId);
                    const isPerItem = type?.calculationMethod === 'per_item';
                    return (
                      <div key={idx} className="flex items-end gap-2">
                        <div className="flex-1 min-w-0">
                          <AirItemTypeTripleDropdown
                            list={airItemTypes}
                            value={row.airItemTypeId}
                            onChange={(id) => updateItem(item.id, idx, 'airItemTypeId', id)}
                            placeholder="เลือกประเภท"
                            buttonClassName="w-full flex items-center justify-between rounded border border-gray-300 bg-white px-3 py-2 text-left text-sm"
                          />
                        </div>
                        {isPerItem && (
                          <div className="w-16 shrink-0">
                            <label className="block text-xs text-gray-500 mb-0.5">จำนวน</label>
                            <input
                              type="number"
                              min="1"
                              value={row.qty}
                              onChange={(e) => updateItem(item.id, idx, 'qty', parseInt(e.target.value) || 1)}
                              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                            />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeItemRow(item.id, idx)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded shrink-0"
                          disabled={(assigningItems[item.id] || []).length <= 1}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Summary with price - only show relevant price (รายการปกติ or รายการฝากส่ง) */}
                {(assigningItems[item.id] || []).some(r => r.airItemTypeId) && (
                  <div className="mt-2 py-2 px-3 bg-gray-50 border rounded text-sm flex flex-wrap gap-x-4 gap-y-1">
                    {(assigningItems[item.id] || []).filter(r => r.airItemTypeId).map((row, idx) => {
                      const price = getTypePrice(row.airItemTypeId);
                      if (!price) return null;
                      const qtyLabel = price.calculationMethod === 'per_item' ? ` x${row.qty}` : '';
                      const amount = isSendOnly ? price.sendOnly : price.normal;
                      const priceTypeLabel = isSendOnly ? 'รายการฝากส่ง' : 'รายการปกติ';
                      const priceTypeClass = isSendOnly ? 'text-blue-600' : 'text-green-600';
                      return (
                        <span key={idx} className="text-gray-700">
                          <span className="font-medium">{getTypeDisplay(row.airItemTypeId)}</span>
                          <span className={`${priceTypeClass} ml-1`}>
                            {priceTypeLabel} ฿{formatAmountRound(amount)}/{price.unit}{qtyLabel}
                          </span>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-between items-center mt-3 pt-2 border-t">
                  <button
                    type="button"
                    onClick={() => addItemRow(item.id)}
                    className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    <PlusIcon className="h-4 w-4" />
                    เพิ่มประเภท
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSave(item.id)}
                    disabled={savingId === item.id}
                    className="flex items-center gap-1 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 text-sm"
                  >
                    <CheckIcon className="h-4 w-4" />
                    {savingId === item.id ? 'กำลังบันทึก...' : 'บันทึก'}
                  </button>
                </div>
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
};

export default HandCarryTypeAssignment;
